import { supabase } from "./supabase";

export const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

export async function authHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("not signed in");
  return { Authorization: `Bearer ${token}` };
}

export interface UploadUrlResponse {
  url: string;
  object_key: string;
  method: "PUT";
  headers: Record<string, string>;
  expires_in_sec: number;
}

export async function getUploadUrl(args: {
  workspace_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
}): Promise<UploadUrlResponse> {
  const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
  const resp = await fetch(`${API_BASE}/api/upload-url`, {
    method: "POST",
    headers,
    body: JSON.stringify(args),
  });
  if (!resp.ok) throw new Error(`upload-url failed: ${await resp.text()}`);
  return resp.json();
}

export interface PlaybackUrlResponse {
  url: string;
  expires_in_sec: number;
}

export async function getPlaybackUrl(meeting_id: string): Promise<PlaybackUrlResponse> {
  const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
  const resp = await fetch(`${API_BASE}/api/playback-url`, {
    method: "POST",
    headers,
    body: JSON.stringify({ meeting_id }),
  });
  if (!resp.ok) throw new Error(`playback-url failed: ${await resp.text()}`);
  return resp.json();
}

export async function dispatchMeeting(meeting_id: string): Promise<{ enqueued: boolean }> {
  const headers = await authHeaders();
  const resp = await fetch(`${API_BASE}/api/meetings/${meeting_id}/dispatch`, {
    method: "POST",
    headers,
  });
  if (!resp.ok) throw new Error(`dispatch failed: ${await resp.text()}`);
  return resp.json();
}

export interface ResubmitResponse {
  enqueued: boolean;
  job_id: string | null;
}

export async function resubmitMeeting(meeting_id: string): Promise<ResubmitResponse> {
  const headers = await authHeaders();
  const resp = await fetch(`${API_BASE}/api/meetings/${meeting_id}/resubmit`, {
    method: "POST",
    headers,
  });
  if (!resp.ok) throw new Error(`resubmit failed: ${await resp.text()}`);
  return resp.json();
}

export interface ReplaceAudioResponse {
  enqueued: boolean;
  job_id: string | null;
}

export async function replaceAudio(
  meeting_id: string,
  args: { object_key: string; size_bytes: number; mime_type: string },
): Promise<ReplaceAudioResponse> {
  const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
  const resp = await fetch(`${API_BASE}/api/meetings/${meeting_id}/replace-audio`, {
    method: "POST",
    headers,
    body: JSON.stringify(args),
  });
  if (!resp.ok) throw new Error(`replace-audio failed: ${await resp.text()}`);
  return resp.json();
}

export interface SubmitContextBundle {
  general?: { key: string; value: string }[] | null;
  terms?: string[] | null;
  text?: string | null;
}

export interface SubmitContextResponse {
  enqueued: boolean;
  job_id: string | null;
}

export async function submitContextForSoniox(
  meeting_id: string,
  context: SubmitContextBundle,
): Promise<SubmitContextResponse> {
  const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
  const resp = await fetch(`${API_BASE}/api/meetings/${meeting_id}/submit-context`, {
    method: "POST",
    headers,
    body: JSON.stringify({ context }),
  });
  if (!resp.ok) throw new Error(`submit-context failed: ${await resp.text()}`);
  return resp.json();
}

export interface ReAnnotateResponse {
  enqueued: boolean;
  job_id: string | null;
}

export async function reAnnotateMeeting(
  meeting_id: string,
  extra_context: string | null,
  run_deeper_attribution: boolean = false,
): Promise<ReAnnotateResponse> {
  const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
  const resp = await fetch(`${API_BASE}/api/meetings/${meeting_id}/re-annotate`, {
    method: "POST",
    headers,
    body: JSON.stringify({ extra_context, run_deeper_attribution }),
  });
  if (!resp.ok) throw new Error(`re-annotate failed: ${await resp.text()}`);
  return resp.json();
}

// Lock / Unlock writes go direct via supabase-js: RLS already gates writes on
// workspace membership and there is no server-side side effect to coordinate.
// The backend retains authority via 409s on /resubmit + /re-annotate when
// locked_at is set; see DECISIONS.md 2026-05-13 lock-in entries.
export async function lockMeeting(meeting_id: string): Promise<void> {
  const { error } = await supabase
    .from("meetings")
    .update({ locked_at: new Date().toISOString() })
    .eq("id", meeting_id);
  if (error) throw new Error(`lock failed: ${error.message}`);
}

export async function unlockMeeting(meeting_id: string): Promise<void> {
  const { error } = await supabase
    .from("meetings")
    .update({ locked_at: null })
    .eq("id", meeting_id);
  if (error) throw new Error(`unlock failed: ${error.message}`);
}

// Parse the filename out of a server Content-Disposition header. RFC 5987
// filename* form is preferred when present (handles UTF-8); falls back to
// the plain filename="..." form. Returns null when neither is present.
function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  // RFC 5987 form: filename*=UTF-8''<url-encoded>
  const star = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (star) {
    try {
      return decodeURIComponent(star[1]);
    } catch {
      // fall through to plain
    }
  }
  const plain = header.match(/filename="([^"]+)"/i);
  if (plain) return plain[1];
  return null;
}

export interface ExportSections {
  transcript: boolean;
  summary: boolean;
}

const DEFAULT_SECTIONS: ExportSections = { transcript: true, summary: false };

export function exportQueryString(sections: ExportSections): string {
  return `transcript=${sections.transcript}&summary=${sections.summary}`;
}

// Trigger a file download by fetching the export endpoint with the user's
// JWT, materializing the response as a Blob, and clicking a hidden anchor.
// The browser respects the Content-Disposition filename when present; we
// also set <a download> from the header for cross-browser safety.
async function downloadExportFile(
  meeting_id: string,
  format: "markdown" | "json" | "docx" | "html",
  sections: ExportSections = DEFAULT_SECTIONS,
): Promise<void> {
  const headers = await authHeaders();
  // Send the browser's IANA timezone so the export's wall-clock timestamps
  // match what the user sees in the app (formatWallClock uses browser-local).
  let tzParam = "";
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) tzParam = `&tz=${encodeURIComponent(tz)}`;
  } catch {
    /* no Intl timezone available — backend falls back to recorded_at's offset */
  }
  const resp = await fetch(
    `${API_BASE}/api/meetings/${meeting_id}/export/${format}?${exportQueryString(sections)}${tzParam}`,
    { headers },
  );
  if (!resp.ok) throw new Error(`export ${format} failed: ${await resp.text()}`);
  const blob = await resp.blob();
  const filename =
    filenameFromContentDisposition(resp.headers.get("Content-Disposition")) ??
    `transcript.${format === "markdown" ? "md" : format}`;
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  // Defer cleanup — do NOT revoke the object URL (or remove the anchor)
  // synchronously. When the browser shows a "Save As" / replace-existing dialog
  // the file is only written AFTER the user confirms, which can be many seconds
  // later; revoking the blob before then cancels the download ("Needs permission
  // to download" on a Word export that was replacing an existing file). Hold the
  // blob alive well past any dialog; it's freed after the timeout or page reload.
  window.setTimeout(() => {
    URL.revokeObjectURL(objUrl);
    a.remove();
  }, 120_000);
}

export async function exportMarkdown(meeting_id: string, sections?: ExportSections): Promise<void> {
  return downloadExportFile(meeting_id, "markdown", sections);
}

export async function exportJson(meeting_id: string, sections?: ExportSections): Promise<void> {
  return downloadExportFile(meeting_id, "json", sections);
}

export async function exportDocx(meeting_id: string, sections?: ExportSections): Promise<void> {
  return downloadExportFile(meeting_id, "docx", sections);
}

export async function exportHtml(meeting_id: string, sections?: ExportSections): Promise<void> {
  return downloadExportFile(meeting_id, "html", sections);
}

export interface AssignProjectResponse {
  ok: boolean;
  attendees_promoted: number;
  terms_promoted: number;
  // Set when the assignment committed but auto-promotion failed (best-effort).
  // The caller surfaces it as a soft notice, not an error — the assign succeeded.
  promotion_warning?: string | null;
}

/**
 * Assign (or un-assign) a meeting to a project, and auto-promote
 * the meeting's names/terms into the destination project's registry.
 *
 * Replaces the direct Supabase `.update({project_id})` pattern.
 * The server handles the project_change event log and the promotion step.
 */
export async function assignMeetingToProject(
  meeting_id: string,
  project_id: string | null,
): Promise<AssignProjectResponse> {
  const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
  const resp = await fetch(`${API_BASE}/api/meetings/${meeting_id}/assign-project`, {
    method: "POST",
    headers,
    body: JSON.stringify({ project_id }),
  });
  if (!resp.ok) throw new Error(`assign-project failed: ${await resp.text()}`);
  return resp.json();
}

export interface AudioDownloadResponse {
  url: string;
  filename: string;
  expires_in_sec: number;
}

// Fetch the signed R2 URL + navigate the browser to it. The R2 response
// carries Content-Disposition: attachment via the response-content-
// disposition query-param override (see r2.presign_download_attachment),
// so the browser saves the file rather than streaming it.
export async function downloadAudio(meeting_id: string): Promise<void> {
  const headers = await authHeaders();
  const resp = await fetch(
    `${API_BASE}/api/meetings/${meeting_id}/audio/download`,
    { headers },
  );
  if (!resp.ok) throw new Error(`audio download failed: ${await resp.text()}`);
  const data: AudioDownloadResponse = await resp.json();
  // window.location.href on the signed URL triggers the browser-direct
  // R2 download. We use a transient anchor instead so the current page
  // stays in place even if the download dialog is dismissed.
  const a = document.createElement("a");
  a.href = data.url;
  a.download = data.filename;
  // R2 is cross-origin from the SPA -- without target="_self" the
  // browser may open in a new tab, but the Content-Disposition forces
  // attachment so it downloads either way.
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

// --- Permanent delete (Archive model). Server-side DELETE endpoints; the
// item must already be archived (deleted_at set) or the server returns 409. ---

export interface PermanentDeleteResult {
  ok: boolean;
  r2_delete_warning: string | null;
}

export async function deleteMeetingPermanently(
  meeting_id: string,
): Promise<PermanentDeleteResult> {
  const resp = await fetch(`${API_BASE}/api/meetings/${meeting_id}`, {
    method: "DELETE",
    headers: await authHeaders(),
  });
  if (!resp.ok) throw new Error(`permanent delete failed: ${await resp.text()}`);
  const body = await resp.json();
  return { ok: !!body.ok, r2_delete_warning: body.r2_delete_warning ?? null };
}

export async function deleteProjectPermanently(project_id: string): Promise<void> {
  const resp = await fetch(`${API_BASE}/api/projects/${project_id}`, {
    method: "DELETE",
    headers: await authHeaders(),
  });
  if (!resp.ok) throw new Error(`permanent delete failed: ${await resp.text()}`);
}

// --- Transcript content search (Task 3) ---

export interface SearchResult {
  meeting_id: string;
  title: string | null;
  snippet: string | null;
}

export interface SearchResponse {
  results: SearchResult[];
}

/**
 * Full-text search across meeting titles and transcript content.
 * Requires q.length >= 2 (server returns 400 otherwise).
 * Returns up to 20 matches, scoped to the caller's workspaces.
 */
export async function searchMeetings(q: string): Promise<SearchResponse> {
  const headers = await authHeaders();
  const url = `${API_BASE}/api/search?q=${encodeURIComponent(q)}`;
  const resp = await fetch(url, { headers });
  if (!resp.ok) throw new Error(`search failed: ${await resp.text()}`);
  return resp.json();
}

export interface GlossaryDuplicateGroup {
  member_ids: string[];
  terms: string[];
  reason: string;
}

export async function findGlossaryDuplicates(
  projectId: string,
): Promise<GlossaryDuplicateGroup[]> {
  const headers = await authHeaders();
  const resp = await fetch(
    `${API_BASE}/api/projects/${projectId}/glossary/find-duplicates`,
    { method: "POST", headers },
  );
  if (!resp.ok) throw new Error(`find-duplicates failed: ${resp.status}`);
  const data = await resp.json();
  return data.groups ?? [];
}

export interface ReworkSectionResponse { enqueued: boolean; job_id: string | null; }

export async function reworkSection(
  meeting_id: string,
  args: { start_ms: number; end_ms: number; hint?: string | null },
): Promise<ReworkSectionResponse> {
  const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
  const resp = await fetch(`${API_BASE}/api/meetings/${meeting_id}/rework-section`, {
    method: "POST", headers, body: JSON.stringify(args),
  });
  if (!resp.ok) throw new Error(`rework-section failed: ${await resp.text()}`);
  return resp.json();
}

export async function regenerateSummary(
  meeting_id: string,
): Promise<{ enqueued: boolean; job_id: string | null }> {
  const headers = await authHeaders();
  const resp = await fetch(`${API_BASE}/api/meetings/${meeting_id}/regenerate-summary`, {
    method: "POST", headers,
  });
  if (!resp.ok) throw new Error(`regenerate-summary failed: ${await resp.text()}`);
  return resp.json();
}
